import { useQuery, useQueryClient } from "@tanstack/react-query";

import type { SubscriptionResponse } from "@/client/services/subscription";
import { subscriptionService } from "@/client/services/subscription";

const defaultSubscription: SubscriptionResponse = {
  hasActiveSubscription: false,
  planType: null,
  expiresAt: null,
  downloadsUsed: 0,
  downloadsAllowed: 0,
  status: "expired",
};

export const useSubscription = (): {
  subscription: SubscriptionResponse;
  isLoading: boolean;
  error: Error | null;
  canDownload: (resumeId: string) => Promise<boolean>;
  refresh: () => Promise<void>;
} => {
  const queryClient = useQueryClient();

  const {
    data: subscription,
    isLoading,
    error,
    refetch,
  } = useQuery<SubscriptionResponse>({
    queryKey: ["subscription"],
    queryFn: subscriptionService.getCurrentSubscription,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });

  const canDownload = async (resumeId: string): Promise<boolean> => {
    try {
      return await subscriptionService.canDownload(resumeId);
    } catch {
      return false;
    }
  };

  const refresh = async () => {
    await refetch();
    // Invalidate related queries
    await queryClient.invalidateQueries({ queryKey: ["subscription"] });
    await queryClient.invalidateQueries({ queryKey: ["payments"] });
  };

  return {
    subscription: subscription ?? defaultSubscription,
    isLoading,
    error: error,
    canDownload,
    refresh,
  };
};
